/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable no-console */
import SelectorEngine from '../../../utils/dom/selector-engine';
import Manipulator from '../../../utils/dom/manipulator';
import Modal from '../../../components/Modal';
import Request from '../../../utils/request';
import Select from '../../../components/Select';
import {removeInvalidClasses, validFormElementValue} from '../../../utils/validateFormElement';
import ValidationUtils from '../../../utils/validation';

const TAB_KEYS = {
	CONFIRMED: 'confirmed',
	WAITLISTED: 'waitlisted',
	CANCELLED: 'cancelled'
};

const ATTENDEE_COLUMNS = [
	{ key: 'firstName', label: 'First name', sortable: true },
	{ key: 'lastName', label: 'Last name', sortable: true },
	{ key: 'email', label: 'Email', sortable: true },
	{ key: 'role', label: 'Tutor' },
	{ key: 'btmNumber', label: 'BTM', sortable: true },
	{ key: 'passed', label: 'Passed' },
	{ key: 'didNotAttend', label: 'Did not attend' },
	{ key: 'method', label: 'Booking', sortable: true },
	{ key: 'eligible', label: 'Eligible', sortable: true },
	{ key: 'comment', label: 'Notes' },
];

const ATTENDEE_FILTER = {
	[TAB_KEYS.CONFIRMED]: (a) => a.status !== 'Cancelled' && a.status !== 'Waitlisted' && a.status !== 'Pending',
	[TAB_KEYS.WAITLISTED]: (a) => a.status === 'Waitlisted',
	[TAB_KEYS.CANCELLED]: (a) => a.status === 'Cancelled'
};

const isResultable = (a) => a.status !== 'Cancelled' && a.status !== 'Waitlisted';

const TAB_SELECTORS = {
	[TAB_KEYS.CONFIRMED]: '.js-course-providers-class-details-confirmed-tab',
	[TAB_KEYS.WAITLISTED]: '.js-course-providers-class-details-waitlisted-tab',
	[TAB_KEYS.CANCELLED]: '.js-course-providers-class-details-cancelled-tab'
};

const DEFAULT_SORT = { key: null, dir: 0 };

class CourseProvidersClassDetails {
	constructor() {
		this._wrapper = SelectorEngine.findOne('.js-course-providers-class-details-wrapper');
		if (!this._wrapper) return;

		this._classID = new URLSearchParams(window.location.search).get('classId') || 'class-id-not-found';
		this._classDetailsApiUrl = Manipulator.getDataAttribute(this._wrapper, 'class-details-api-url');
		this._classStatusUpdateApiUrl = Manipulator.getDataAttribute(this._wrapper, 'class-status-update-api-url');
		this._attendeeCommentsApiUrl = Manipulator.getDataAttribute(this._wrapper, 'attendee-comments-api-url');
		this._saveAttendeesApiUrl = Manipulator.getDataAttribute(this._wrapper, 'save-attendees-api-url');
		this._moveToConfirmedApiUrl = Manipulator.getDataAttribute(this._wrapper, 'move-to-confirmed-api-url');
		this._moveToWaitListApiUrl = Manipulator.getDataAttribute(this._wrapper, 'move-to-wait-list-api-url');
		this._moveToCancelledApiUrl = Manipulator.getDataAttribute(this._wrapper, 'move-to-cancelled-api-url');
		this._attendeeSearchApiUrl = Manipulator.getDataAttribute(this._wrapper, 'attendee-search-api-url');
		this._addAttendeeApiUrl = Manipulator.getDataAttribute(this._wrapper, 'add-attendee-api-url');
		this._manageClassPageUrl = Manipulator.getDataAttribute(this._wrapper, 'manage-class-page-url');
		this._editClassBtn = this._wrapper.querySelector('.js-edit-class');
		this._classDetailsContainer = this._wrapper.querySelector('.js-course-providers-class-details-container');
		this._classInfoContainer = this._classDetailsContainer.querySelector('.js-course-providers-class-details-info');
		this._classNameContainer = this._classDetailsContainer.querySelector('.js-course-providers-class-details-name');
		this._wrapperLoading = this._wrapper.querySelector('.js-course-providers-class-details-wrapper-loading');
		this._wrapperEmpty = this._wrapper.querySelector('.js-course-providers-class-details-wrapper-empty');
		this._saveResultsContainer = this._wrapper.querySelector('.js-class-save-results');
		this._saveBtn = this._wrapper.querySelector('.js-save-results-btn');
		this._saveBtnContainer = this._wrapper.querySelector('.js-save-results-btn-container');
		this._submitBtn = this._wrapper.querySelector('.js-submit-results-btn');
		this._submitBtnContainer = this._wrapper.querySelector('.js-submit-results-btn-container');
		this._resultMsg = this._wrapper?.querySelector('.js-save-results-msg');
		this._statusSelect = this._wrapper.querySelector('.js-class-status-select');
		const select = this._statusSelect.querySelector('select');
		if (select) select.addEventListener('change', () => this._changeClassStatus(select.value));
		this._statusReadonly = this._wrapper.querySelector('.js-class-status-readonly');
		this._statusUpdating = this._wrapper.querySelector('.js-class-status-updating');
		this._editLink = this._wrapper.querySelector('.js-class-edit-link');
		this._currentClass = {};

		// Tabs
		this._tabButtons = {
			confirmed: this._wrapper.querySelector('#lta-class-tab-1'),
			waitlisted: this._wrapper.querySelector('#lta-class-tab-2'),
			cancelled: this._wrapper.querySelector('#lta-class-tab-3')
		};
		this._attendeeSort = JSON.parse(JSON.stringify(DEFAULT_SORT));
		this._selectedAttendees = new Set();
		this._activeTab = 'confirmed';
		Object.entries(this._tabButtons).forEach(([key, btn]) => {
			if (btn) btn.addEventListener('click', () => {
				this._activeTab = key;
				this._selectedAttendees.clear();
				this._updateActionsUI();
				this._updateTabTableUI(key);
			});
		});

		// Action bar
		this._actionsBar = this._wrapper.querySelector('.js-attendee-actions');
		this._actionLoading = this._wrapper.querySelector('.js-action-loading');
		this._actionEmailBtn = this._wrapper.querySelector('.js-action-email');
		this._actionExportBtn = this._wrapper.querySelector('.js-action-export');
		this._actionConfirmedBtn = this._wrapper.querySelector('.js-action-confirmed');
		this._actionConfirmedBtnContainer = this._wrapper.querySelector('.js-action-confirmed-container');
		this._actionWaitlistedBtn = this._wrapper.querySelector('.js-action-waitlisted');
		this._actionWaitlistedBtnContainer = this._wrapper.querySelector('.js-action-waitlisted-container');
		this._actionCanceleedBtn = this._wrapper.querySelector('.js-action-cancelled');
		this._actionCanceleedBtnContainer = this._wrapper.querySelector('.js-action-cancelled-container');
		this._actionEmailBtn?.addEventListener('click', () => this._actionEmail());
		this._actionExportBtn?.addEventListener('click', () => this._actionExport());
		this._actionConfirmedBtn?.addEventListener('click', () => this._actionMove('Confirmed'));
		this._actionWaitlistedBtn?.addEventListener('click', () => this._actionMove('Waitlisted'));
		this._actionCanceleedBtn?.addEventListener('click', () => this._actionMove('Cancelled'));

		this._saveBtn?.addEventListener('click', () => this._saveResults(false));
		this._submitBtn?.addEventListener('click', () => this._saveResults(true));

		// Add Attendee modal
		this._addAttendeeBtnContainer = this._wrapper.querySelector('.js-add-attendee-btn-container');
		this._addAttendeeBtn = this._wrapper.querySelector('.js-add-attendee-btn');
		this._addAttendeeModalEl = this._wrapper.querySelector('#add-attendee-modal');
		this._addAttendeeModal = this._addAttendeeModalEl ? new Modal(this._addAttendeeModalEl) : null;
		this._addAttendeeSearchPanel = this._wrapper.querySelector('.js-add-attendee-search');
		this._addAttendeeResultsPanel = this._wrapper.querySelector('.js-add-attendee-results');
		this._addAttendeeloading = this._wrapper.querySelector('.js-add-attendee-loading');
		this._addAttendeeNotAdded = this._wrapper.querySelector('.js-attendee-not-added');
		this._addAttendeeMatchesContainer = this._wrapper.querySelector('.js-attendee-matches');
		this._addAttendeeNoMatches = this._wrapper.querySelector('.js-no-matches');
		this._addAttendeeSummary = this._wrapper.querySelector('.js-search-summary');
		this._addAttendeeFirstName = this._wrapper.querySelector('#attendee-first-name');
		this._addAttendeeLastName = this._wrapper.querySelector('#attendee-last-name');
		this._addAttendeeDayOfBirth = this._wrapper.querySelector('#attendee-day-of-birth');
		this._addAttendeeMonthOfBirth = this._wrapper.querySelector('#attendee-month-of-birth');
		this._addAttendeeYearOfBirth = this._wrapper.querySelector('#attendee-year-of-birth');
		this._addAttendeeBTM = this._wrapper.querySelector('#attendee-btm');
		this._addAttendeeCoachCode = this._wrapper.querySelector('#attendee-coach-code');
		this._addAttendeeBtn?.addEventListener('click', () => {
			this._resetAddAttendeeModal();
			this._addAttendeeModal?.show();
		});
		this._searchRules = {
			firstName: { input: this._addAttendeeFirstName, errorMessage: 'First name is required' },
			lastName: { input: this._addAttendeeLastName, errorMessage: 'Last name is required' },
			day: { input: this._addAttendeeDayOfBirth, errorMessage: 'Day is required' },
			month: { input: this._addAttendeeMonthOfBirth, errorMessage: 'Month is required' },
			year: { input: this._addAttendeeYearOfBirth, errorMessage: 'Year is required' },
			btm: { input: this._addAttendeeBTM, errorMessage: 'BTM Number is required', hasLimit: true, charLimit: 9, minLengthErrorMessage: 'Must be 9 characters' },
			coachCode: { input: this._addAttendeeCoachCode, errorMessage: 'Coach code is required' },
		};

		window.addEventListener('scroll', () => this._closeCommentEditor(), { passive: true });
		window.addEventListener('resize', () => this._closeCommentEditor(), { passive: true });

		this._editClassBtn.href = `${this._manageClassPageUrl}?classId=${this._classID}`;

		this._initAddAttendeeModal();
		this._initValidation();
		this._fetchClassDetails();
	}

	_cap(s) {
		return s.charAt(0).toUpperCase() + s.slice(1)}
	;

	_updateClassInfoUI() {
		const d = this._currentClass;

		const fields = [
			{ label: 'Start Date', value: d.startDate ? new Date(d.startDate).toLocaleDateString('en-GB') : '' },
			{ label: 'Capacity', value: d.maximumAttendees },
			{ label: 'Price', value: d.cost != null ? `£${d.cost.toFixed(2)}` : '£0.00' },
			{ label: 'Bookings', value: d.onlineEnabled ? 'Online via LTA' : 'External' },
			{ label: 'Venue', value: d.venueName === 'Online' ? '' : d.venueName },
			{ label: 'Waiting List', value: d.waitlistEnabled ? 'Enabled' : 'Not enabled' }
		];

		if (this._classInfoContainer) {
			this._classInfoContainer.innerHTML = `
			<ul class="lta-lists u-mt-3 u-flex u-flex-wrap u-align-center u-gap-2" style="flex-wrap: wrap;white-space:nowrap;">
				${fields
					.filter((f) => f.value != null)
					.map((f) => `
					<li class='u-h4'>
						<span class="u-mr-1" style="font-weight: bold;">${f.label}:</span>
						<span style='font-weight: normal;'>${f.value}</span>
					</li>
					`).join('')}
			</ul>
		`;
		}
		if (this._classNameContainer) {
			this._classNameContainer.innerHTML = `
				${d.courseName}
				${d.availableInLTALearn ? `<span class='u-text-lta-blue'>${d.sfClassId}</span>` : ''}
			`;
		}
	}

	isClassCompletedOrAwaitingResults() {
		const s = this._currentClass.status;
		return s === 'Completed' || s === 'AwaitingResults';
	}

	_getTabAttendees(tabKey) {
		const attendees = this._currentClass.attendees || [];
		const fn = ATTENDEE_FILTER[tabKey];
		return fn ? attendees.filter(fn) : [...attendees];
	}

	_applySort(items) {
		const { key, dir } = this._attendeeSort;
		if (!key || dir === 0) return items;
		return [...items].sort((a, b) => {
			const va = a[key] ?? '';
			const vb = b[key] ?? '';
			const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
			return dir === 1 ? cmp : -cmp;
		});
	}

	_sortIndicator(key) {
		if (this._attendeeSort.key !== key || this._attendeeSort.dir === 0) return '';
		return this._attendeeSort.dir === 1 ? ' ▲' : ' ▼';
	}

	_updateTabCountsUI() {
		const attendees = this._currentClass.attendees || [];
		const counts = Object.fromEntries(
			Object.entries(ATTENDEE_FILTER).map(([key, fn]) => [key, attendees.filter(fn).length])
		);

		Object.entries(this._tabButtons).forEach(([key, btn]) => {
			if (!btn) return;
			const title = btn.querySelector('.title');
			if (title) title.textContent = `${title.textContent.replace(/\s*\(\d+\)$/, '')} (${counts[key]})`;
		});
	}

	_updateTabTableUI(tabKey) {
		const tabEl = this._wrapper.querySelector(TAB_SELECTORS[tabKey]);
		if (!tabEl) return;

		const tableContainer = tabEl.querySelector('.js-class-attendees-table');
		const emptyMsg = tabEl.querySelector('.js-class-attendees-empty');
		if (!tableContainer) return;

		const processedAttendees = this._applySort(this._getTabAttendees(tabKey));

		if (!processedAttendees.length) {
			tableContainer.innerHTML = '';
			if (emptyMsg) emptyMsg.hidden = false;
			return;
		}
		if (emptyMsg) emptyMsg.hidden = true;

		const cols = ATTENDEE_COLUMNS.filter((c) => {
			if (['passed', 'didNotAttend'].includes(c.key)) return this.isClassCompletedOrAwaitingResults();
			if (c.key === 'method' || c.key === 'comment') return !this.isClassCompletedOrAwaitingResults();
			if (c.key === 'eligible') return !this.isClassCompletedOrAwaitingResults() && this._currentClass.status !== 'InProgress';
			return true;
		});

		const allChecked = !this.isClassCompletedOrAwaitingResults() && processedAttendees.length > 0 && processedAttendees.every((a) => this._selectedAttendees.has(a.id));

		const checkboxHeader = !this.isClassCompletedOrAwaitingResults() ? `<th class="u-text-center" style='width: 45px'><input type="checkbox" aria-label="Select all" class="js-check-all lta-form-check-input" ${allChecked ? 'checked' : ''} /></th>` : '';
		const headers =
			checkboxHeader +
			cols
			.map((col) =>
				col.sortable
					? `<th style="cursor:pointer" data-sort-key="${col.key}" aria-sort="${this._attendeeSort.key === col.key && this._attendeeSort.dir === 1 ? 'ascending' : this._attendeeSort.key === col.key && this._attendeeSort.dir === 2 ? 'descending' : 'none'}">${col.label}${this._sortIndicator(col.key)}</th>`
					: `<th${['passed', 'didNotAttend'].includes(col.key) ? ' class="u-text-center"' : ''}>${col.label}</th>`
			)
			.join('');

		const rows = processedAttendees
			.map((a, ri) => {
				const checkbox = !this.isClassCompletedOrAwaitingResults()
					? `
					<td class="u-text-center">
						<input type="checkbox" class="js-row-check lta-form-check-input" aria-label="Select ${a.firstName} ${a.lastName}" data-attendee-id="${a.id}" ${this._selectedAttendees.has(a.id) ? 'checked' : ''} />
					</td>`
					: '';
				const cells = cols
					.map((col, ci) => {
						if (['passed', 'didNotAttend'].includes(col.key)) {
							const radioDisabled =
								this._currentClass.preventSubmitFlag ||
								this._currentClass.status === 'Completed' ||
								(this._currentClass.status === 'AwaitingResults' && (a.status === 'Waitlisted' || a.status === 'Cancelled'));
							return `
							<td class="u-text-center">
								<input class='lta-form-check-input' type="radio" name="result-${a.id}" aria-label="${col.label} for ${a.firstName} ${a.lastName}" value="${this._cap(col.key)}" ${a.status.toLowerCase() === col.key.toLowerCase() ? 'checked' : ''} ${radioDisabled ? 'disabled' : ''} />
							</td>
							`;
						}
						if (col.key === 'btmNumber') return `<td>${a.btmNumber === 0 ? '' : a.btmNumber}</td>`;
						if (col.key === 'eligible') return `<td>${a.eligible ? 'Yes' : 'No'}</td>`;
						if (col.key === 'method') return `<td>${a.method || ''}</td>`;
						if (col.key === 'comment')
							return `
							<td class="p-course-providers-class-details__comment-cell u-text-center">
								<button class="js-comment-toggle p-course-providers-class-details__comment-icon ${a.comment ? 'has-comment' : ''}" data-attendee-id="${a.id}" aria-label="${a.comment ? 'Update note' : 'Add a note'} for ${a.firstName} ${a.lastName}" aria-expanded="false">
									<svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor"><path d="M14 1H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3l3 3 3-3h3a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM4 5h8v1H4V5zm0 2h6v1H4V7z"/></svg>
								</button>
							</td>
						`;
						return `<td>${a[col.key] || ''}</td>`;
					})
					.join('');
				return `<tr>${checkbox}${cells}</tr>`;
			})
			.join('');

		tableContainer.innerHTML = `
			<div class="p-course-providers-class-details__details-table">
				<table>
					<thead><tr>${headers}</tr></thead>
					<tbody>${rows}</tbody>
				</table>
			</div>
		`;

		tableContainer.querySelectorAll('th[data-sort-key]').forEach((th) => {
			th.addEventListener('click', () => {
				const k = th.dataset.sortKey;
				const nextDir = this._attendeeSort.key === k ? (this._attendeeSort.dir + 1) % 3 : 1;
				this._attendeeSort = { key: nextDir === 0 ? null : k, dir: nextDir };
				this._updateAttendeesUI();
			});
		});

		const checkAll = tableContainer.querySelector('.js-check-all');
		if (checkAll) {
			checkAll.addEventListener('change', () => {
				processedAttendees.forEach((a) =>
					checkAll.checked ? this._selectedAttendees.add(a.id) : this._selectedAttendees.delete(a.id)
				);
				this._updateTabTableUI(tabKey);
				this._updateActionsUI();
			});
		}

		tableContainer.querySelectorAll('.js-row-check').forEach((cb) => {
			cb.addEventListener('change', () => {
				const id = cb.dataset.attendeeId;
				cb.checked ? this._selectedAttendees.add(id) : this._selectedAttendees.delete(id);
				const allEl = tableContainer.querySelector('.js-check-all');
				if (allEl) allEl.checked = processedAttendees.every((a) => this._selectedAttendees.has(a.id));
				this._updateActionsUI();
			});
		});

		tableContainer.querySelectorAll('.js-comment-toggle').forEach((btn) => {
			btn.addEventListener('click', () => {
				const id = btn.dataset.attendeeId;
				const existing = btn.parentElement.querySelector('.js-comment-editor');
				this._closeCommentEditor();
				if (existing) return;
				const attendee = (this._currentClass.attendees || []).find((a) => a.id === id);
				const editor = document.createElement('div');
				editor.className = 'p-course-providers-class-details__comment-editor js-comment-editor';
				editor.style.cssText = 'position:fixed;z-index:1000;';
				editor.innerHTML = `
					<textarea class="js-comment-textarea" rows="3" name="note" aria-label="Note for ${attendee?.firstName || ''} ${attendee?.lastName || ''}">${attendee?.comment || ''}</textarea>
					<button class="lta-btn lta-btn--sm lta-btn--line js-comment-save">Save & Close</button>
				`;
				btn.after(editor);
				const rect = btn.getBoundingClientRect();
				editor.style.top = `${rect.bottom - editor.offsetHeight}px`;
				editor.style.left = `${rect.left - editor.offsetWidth}px`;
				btn.setAttribute('aria-expanded', 'true');
				editor.querySelector('.js-comment-save').addEventListener('click', () => {
					this._saveComment(id, editor.querySelector('.js-comment-textarea').value, tabKey);
					this._closeCommentEditor();
				});
			});
		});

		tableContainer.querySelector('.p-course-providers-class-details__details-table')
			?.addEventListener('scroll', () => this._closeCommentEditor(), { passive: true });

		tableContainer.querySelectorAll('input[type="radio"][name^="result-"]').forEach((radio) => {
			radio.addEventListener('change', () => {
				const attendee = (this._currentClass.attendees || []).find((a) => a.id === radio.name.replace('result-', ''));
				if (attendee) attendee.status = radio.value;
				this._updateSaveResultsUI();
			});
		});
	}

	_updateAttendeesUI() {
		this._updateTabCountsUI();
		Object.keys(TAB_SELECTORS).forEach((tabKey) => this._updateTabTableUI(tabKey));
	}

	_closeCommentEditor() {
		const editor = this._wrapper.querySelector('.js-comment-editor');
		if (editor) editor.remove();
		this._wrapper.querySelectorAll('.js-comment-toggle').forEach((b) => b.setAttribute('aria-expanded', 'false'));
	}

	_getSelectedAttendees() {
		return (this._currentClass.attendees || []).filter((a) => this._selectedAttendees.has(a.id));
	}

	_updateActionsUI() {
		const hasAttendees = (this._currentClass.attendees || []).length > 0;
		const isCompleted = this.isClassCompletedOrAwaitingResults();
		this._actionsBar.hidden = !hasAttendees || isCompleted;

		const hasSelection = this._selectedAttendees.size > 0;
		const buttons = [this._actionEmailBtn, this._actionExportBtn, this._actionConfirmedBtn, this._actionWaitlistedBtn, this._actionCanceleedBtn];
		buttons.forEach((btn) => { if (btn) btn.disabled = !hasSelection; });

		const tab = this._activeTab;
		if (this._actionConfirmedBtnContainer) this._actionConfirmedBtnContainer.hidden = tab === 'confirmed';
		if (this._actionWaitlistedBtnContainer) this._actionWaitlistedBtnContainer.hidden = tab === 'waitlisted';
		if (this._actionCanceleedBtnContainer) this._actionCanceleedBtnContainer.hidden = tab === 'cancelled';
	}

	_actionEmail() {
		const emails = this._getSelectedAttendees().map((a) => a.email).filter(Boolean);
		if (emails.length) window.location.href = `mailto:${emails.join(';')}`;
	}

	_actionExport() {
		const rows = this._getSelectedAttendees().map((a) => ({
			FIRST_NAME: a.firstName,
			LAST_NAME: a.lastName,
			EMAIL_ADDRESS: a.email,
			ROLE: a.role,
			BTM_NUMBER: a.btmNumber,
			BOOKING_METHOD: a.method,
			COACH_TUTOR: a.coachTutor ? 'Yes' : 'No',
			ELIGIBLE: a.eligible ? 'Yes' : 'No',
			NOTES: a.comment
		}));
		if (!rows.length) return;
		const headers = Object.keys(rows[0]);
		const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => `="${(r[h] ?? '').toString()}"`).join(','))].join('\r\n');
		const blob = new Blob([csv], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'LTA_Course_Attendees.csv';
		a.click();
		URL.revokeObjectURL(url);
	}

	_resetAddAttendeeModal() {
		if (!this._addAttendeeModalEl) return;
		this._addAttendeeSearchPanel.hidden = false;
		this._addAttendeeResultsPanel.hidden = true;
		this._addAttendeeloading.hidden = true;
		this._addAttendeeNotAdded.hidden = true;
		this._addAttendeeNoMatches.hidden = true;
		this._addAttendeeMatchesContainer.innerHTML = '';

		[this._addAttendeeFirstName, this._addAttendeeLastName, this._addAttendeeDayOfBirth, this._addAttendeeMonthOfBirth, this._addAttendeeYearOfBirth, this._addAttendeeBTM, this._addAttendeeCoachCode].forEach((input) => {
			if (!input) return;
			if (input.tagName === 'SELECT') {
				const selectInstance = Select.getInstance(input.parentElement);
				if (selectInstance) selectInstance.reset();
			} else {
				input.value = '';
			}
			const wrapper = input.closest(ValidationUtils.getFieldWrapper(input.type));
			if (!wrapper) return;
			removeInvalidClasses(wrapper);
			const errorEl = wrapper.querySelector('.lta-text-input--error');
			if (errorEl) errorEl.innerHTML = '';
		});

		const dobRadio = this._addAttendeeModalEl.querySelector('.js-search-by[value="dateOfBirth"]');
		if (dobRadio) {
			dobRadio.checked = true;
			dobRadio.dispatchEvent(new Event('change'));
		}
	}

	_initValidation() {
		Object.values(this._searchRules).forEach((rule) => {
			const event = rule.input?.tagName === 'SELECT' ? 'change' : 'input';
			rule.input?.addEventListener(event, () => this._validateField(rule));
		});
	}

	_validateField = (rule) => {
		const { input, ...opts } = rule;
		if (input?.closest('[hidden]')) return true;
		const value = input?.value?.trim();
		return validFormElementValue(value, { element: input?.closest(ValidationUtils.getFieldWrapper(input?.type)), ...opts });
	};

	_validateForm() {
		return Object.values(this._searchRules).map((rule) => this._validateField(rule)).every(Boolean);
	}

	_initAddAttendeeModal() {
		if (!this._addAttendeeModal) return;

		// Toggle search-by fields
		this._addAttendeeModalEl.querySelectorAll('.js-search-by').forEach((radio) => {
			radio.addEventListener('change', () => {
				this._addAttendeeModalEl.querySelector('.js-search-field-dob').hidden = radio.value !== 'dateOfBirth';
				this._addAttendeeModalEl.querySelector('.js-search-field-btm').hidden = radio.value !== 'btmNumber';
				this._addAttendeeModalEl.querySelector('.js-search-field-coach').hidden = radio.value !== 'coachCode';
			});
		});

		// Back to search
		this._addAttendeeModalEl.querySelector('.js-back-to-search')?.addEventListener('click', (e) => {
			e.preventDefault();
			this._addAttendeeSearchPanel.hidden = false;
			this._addAttendeeResultsPanel.hidden = true;
		});

		// Search
		this._addAttendeeModalEl.querySelector('.js-attendee-search-btn')?.addEventListener('click', () => {
			this._findMatches()
		})
	}

	_showAttendeeResults(res, searchParams) {
		this._addAttendeeSearchPanel.hidden = true;
		this._addAttendeeResultsPanel.hidden = false;
		this._addAttendeeNotAdded.hidden = true;

		this._addAttendeeSummary.textContent = `You searched for ${searchParams.firstName} ${searchParams.lastName} (${searchParams.dateOfBirth || searchParams.bTMNumber || searchParams.coachCode})`;
		const attendees = res?.responseContainerContents?.attendees || [];

		if (!attendees.length) {
			this._addAttendeeMatchesContainer.innerHTML = '';
			this._addAttendeeNoMatches.hidden = false;
			return;
		}
		this._addAttendeeNoMatches.hidden = true;

		this._addAttendeeMatchesContainer.innerHTML = `
			<h3 class="u-title4 u-mb-1">Below are your matches</h3>
			<div class="p-course-providers-class-details__attendee-table">
				<table>
					<thead>
						<tr>
							<th>Name</th>
							<th>BTM</th>
							<th>Eligible?</th>
							<th>Tutor?</th>
							<th style="width: 165px;"><span class="u-hidden-visually">Actions</span></th>
						</tr>
					</thead>
					<tbody>
						${attendees.map((a, i) => `
							<tr>
								<td>${a.firstName} ${a.lastName}</td>
								<td>${a.btmNumber === '0' ? '' : a.btmNumber}</td>
								<td class="u-text-center">${a.eligible
									? '<svg role="img" aria-label="Eligible" style="display: block; margin: 0 auto;" width="24" height="24" viewBox="0 0 16 16" fill="#22c55e"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm3.78 4.97-4.53 4.53L5.22 7.47a.75.75 0 0 0-1.06 1.06l2.56 2.56a.75.75 0 0 0 1.06 0l5.06-5.06a.75.75 0 0 0-1.06-1.06z"/></svg>'
									: '<svg role="img" aria-label="Not eligible" style="display: block; margin: 0 auto;" width="24" height="24" viewBox="0 0 16 16" fill="#ef4444"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm2.78 4.97a.75.75 0 0 0-1.06 0L8 6.69 6.28 4.97a.75.75 0 0 0-1.06 1.06L6.94 7.75 5.22 9.47a.75.75 0 1 0 1.06 1.06L8 8.81l1.72 1.72a.75.75 0 1 0 1.06-1.06L9.06 7.75l1.72-1.72a.75.75 0 0 0 0-1.06z"/></svg>'}</td>
								<td class="u-text-center">${a.coachTutor
									? `<input style="display: block; margin: 0 auto;" type="checkbox" class="js-is-tutor lta-form-check-input" data-idx="${i}" aria-label="Assign ${a.firstName} ${a.lastName} as tutor" />`
									: '<svg role="img" aria-label="Not a tutor" style="display: block; margin: 0 auto;" width="24" height="24" viewBox="0 0 16 16" fill="#ef4444"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm2.78 4.97a.75.75 0 0 0-1.06 0L8 6.69 6.28 4.97a.75.75 0 0 0-1.06 1.06L6.94 7.75 5.22 9.47a.75.75 0 1 0 1.06 1.06L8 8.81l1.72 1.72a.75.75 0 1 0 1.06-1.06L9.06 7.75l1.72-1.72a.75.75 0 0 0 0-1.06z"/></svg>'}
								</td>
								<td>
									<div class="js-add-attendee-confirm-container" data-idx="${i}" ${!a.eligible ? 'hidden' : ''}>
										<button class="lta-btn lta-btn--sm lta-btn--primary js-add-attendee-confirm" data-idx="${i}" style="white-space:nowrap;" aria-label="Add ${a.firstName} ${a.lastName} to course">+ Add to course</button>
									</div>
									${!a.eligible ? `<ul class="u-title4 lta-lists u-m-0 js-ineligible-reasons" data-idx="${i}">${a.ineligibleReasons || ''}</ul>` : ''}
								</td>
							</tr>
						`).join('')}
					</tbody>
				</table>
			</div>
		`;

		this._addAttendeeMatchesContainer.querySelectorAll('.js-is-tutor').forEach((cb) => {
			cb.addEventListener('change', () => {
				const idx = cb.dataset.idx;
				const a = attendees[Number(idx)];
				const addBtnContainer = this._addAttendeeMatchesContainer.querySelector(`.js-add-attendee-confirm-container[data-idx="${idx}"]`);
				const reasons = this._addAttendeeMatchesContainer.querySelector(`.js-ineligible-reasons[data-idx="${idx}"]`);
				if (addBtnContainer) addBtnContainer.hidden = !a.eligible && !cb.checked;
				if (reasons) reasons.hidden = cb.checked;
			});
		});

		this._addAttendeeMatchesContainer.querySelectorAll('.js-add-attendee-confirm').forEach((btn) => {
			btn.addEventListener('click', () => {
				const idx = Number(btn.dataset.idx);
				const attendee = attendees[idx];
				const tutorCheckbox = this._addAttendeeMatchesContainer.querySelector(`.js-is-tutor[data-idx="${idx}"]`);
				if (tutorCheckbox) attendee.isTutor = tutorCheckbox.checked;
				if (!attendee.eligible && !attendee.isTutor) return;
				this._addAttendee(attendee);
			});
		});
	}

	_updateAddAttendeeBtnUI() {
		if (!this._addAttendeeBtnContainer) return;
		const s = this._currentClass.status;
		this._addAttendeeBtnContainer.hidden = ['Cancelled', 'Completed', 'FullNoWaitList', 'FullClosed'].includes(s);
	}

	_getStatusOptions(status) {
		const isInProgress = status === 'InProgress';
		const all = [
			{ value: 'AcceptingBookings', label: 'Accepting Bookings', show: !isInProgress },
			{ value: 'FullWaitList', label: 'Full (Waiting list active)', show: !isInProgress },
			{ value: 'FullNoWaitList', label: 'Full (No waiting list)', show: !isInProgress },
			{ value: 'FullClosed', label: 'Full (Closed)', show: status !== 'Hidden' && !isInProgress },
			{ value: 'Hidden', label: 'Hidden', show: !isInProgress },
			{ value: 'Cancelled', label: 'Cancelled', show: !isInProgress },
			{ value: 'AwaitingResults', label: 'Awaiting Results', show: isInProgress },
			{ value: 'InProgress', label: 'In Progress', show: isInProgress }
		];
		// Always include current status so it can appear as selected value
		return all.filter((o) => o.show || o.value === status);
	}

	_updateStatusUI() {
		const status = this._currentClass.status;
		const isReadonly = ['Completed', 'AwaitingResults', 'Cancelled'].includes(status);

		this._statusSelect.hidden = isReadonly;
		this._statusReadonly.hidden = !isReadonly;
		if (isReadonly) {
			const labels = { Completed: 'Completed', AwaitingResults: 'Awaiting Results', Cancelled: 'Cancelled' };
			this._statusReadonly.innerHTML = `<span>Status: </span><span style='font-weight: normal'>${labels[status] || status}</span>`;
		}

		this._editLink.hidden = !['AcceptingBookings', 'Hidden'].includes(status);

		if (!isReadonly) {
			const selectWrapper = this._statusSelect.querySelector('.js-lta-select');
			if (!selectWrapper) return;

			const nativeSelect = SelectorEngine.findOne('select', selectWrapper);
			if (!nativeSelect) return;

			const options = this._getStatusOptions(status);

			nativeSelect.innerHTML = '';
			for (const o of options) {
				const option = document.createElement('option');
				option.value = o.value;
				option.dataset.ssId = o.value;
				option.textContent = o.label;
				if (o.value === status) option.setAttribute('selected', '');
				nativeSelect.append(option);
			}

			const selectInstance = Select.getInstance(selectWrapper);
			selectInstance.clearItems();
			selectInstance.generateItems();
		}
	}

	_updateSaveResultsUI() {
		const status = this._currentClass.status;
		const isAwaitingResults = status === 'AwaitingResults';
		const attendees = (this._currentClass.attendees || []).filter(isResultable);
		const allHaveResult = attendees.length > 0 && attendees.every((a) => ['Passed', 'Failed', 'DidNotAttend'].includes(a.status));

		this._saveResultsContainer.hidden = !isAwaitingResults || attendees.length === 0;

		if (this._saveBtnContainer) this._saveBtnContainer.hidden = !!this._currentClass.preventSubmitFlag;
		if (this._submitBtnContainer) this._submitBtnContainer.hidden = !allHaveResult;
	}

	_showResultMessage(el, text) {
		if (!el) return;
		el.textContent = text;
		el.hidden = false;
	}

	async _findMatches() {
		if (!this._validateForm()) return;
		const searchBy = this._addAttendeeModalEl.querySelector('.js-search-by:checked')?.value || 'dateOfBirth';
		const params = { firstName: this._addAttendeeFirstName.value.trim(), lastName: this._addAttendeeLastName.value.trim(), classId: this._currentClass.id };

		if (searchBy === 'dateOfBirth') {
			params.dateOfBirth = `${this._addAttendeeYearOfBirth.value}-${this._addAttendeeMonthOfBirth.value}-${this._addAttendeeDayOfBirth.value}`;
		} else if (searchBy === 'btmNumber') {
			params.bTMNumber = this._addAttendeeBTM.value;
		} else if (searchBy === 'coachCode') {
			params.coachCode = this._addAttendeeCoachCode.value;
		}

		this._addAttendeeSearchPanel.hidden = true;
		this._addAttendeeloading.textContent = 'Searching...'
		this._addAttendeeloading.hidden = false;
		try {
			const res = await Request.post(this._attendeeSearchApiUrl, params);
			this._showAttendeeResults(res, params);
		} catch (error) {
			console.error('Attendee search failed:', error);
			this._showAttendeeResults({}, params);
		} finally {
			this._addAttendeeloading.hidden = true;
		}
	}

	async _actionMove(targetStatus) {
		const selected = this._getSelectedAttendees();
		if (!selected.length) return;

		let url;
		if (targetStatus === 'Confirmed') url = this._moveToConfirmedApiUrl;
		if (targetStatus === 'Waitlisted') url = this._moveToWaitListApiUrl;
		if (targetStatus === 'Cancelled') url = this._moveToCancelledApiUrl;
		if (!url) return;

		this._actionLoading.hidden = false;
		try {
			await Request.post(url, {
				attendeeIdList: selected.map((a) => a.id),
				classId: this._currentClass.id
			});

			selected.forEach((a) => { a.status = targetStatus; });
			this._selectedAttendees.clear();
			this._updateAttendeesUI();
		} catch (error) {
			console.error(`Failed to move attendees to ${targetStatus}:`, error);
		} finally {
			this._actionLoading.hidden = true;
			this._updateActionsUI();
		}
	}

	async _addAttendee(attendee) {
		this._addAttendeeSearchPanel.hidden = true;
		this._addAttendeeResultsPanel.hidden = true;
		this._addAttendeeloading.textContent = 'Updating...'
		this._addAttendeeloading.hidden = false;
		try {
			const res = await Request.post(this._addAttendeeApiUrl, {
				classId: this._currentClass.id,
				attendeeId: attendee.id,
				btmNumber: attendee.btmNumber,
				isTutor: attendee.isTutor || false
			});

			if (res?.responseContainerContents?.attendeeAdded) {
				const confirmedCount = (this._currentClass.attendees || []).filter((a) => ATTENDEE_FILTER[TAB_KEYS.CONFIRMED](a) && !a.isTutor).length;
				attendee.status = this._currentClass.status?.includes('Full') || confirmedCount >= this._currentClass.maximumAttendees ? 'Waitlisted' : 'Confirmed';
				attendee.comment = '';
				attendee.method = 'Manual';
				attendee.role = attendee.isTutor ? 'Yes' : 'No';
				this._currentClass.attendees.unshift(attendee);
				this._addAttendeeModal.hide();
				this._updateAttendeesUI();
				this._updateActionsUI();
			} else {
				this._addAttendeeloading.hidden = true;
				this._addAttendeeResultsPanel.hidden = false;
				this._addAttendeeNotAdded.textContent = res?.attendeeNotAddedReason || 'Could not add attendee';
				this._addAttendeeNotAdded.hidden = false;
			}
		} catch (error) {
			console.error('Failed to add attendee:', error);
			this._addAttendeeloading.hidden = true;
			this._addAttendeeResultsPanel.hidden = false;
			this._addAttendeeNotAdded.textContent = 'Could not add attendee';
			this._addAttendeeNotAdded.hidden = false;
		}
	}

	async _saveResults(submit = false) {
		if (this._resultMsg) this._showResultMessage(this._resultMsg, 'Saving...');
		if (this._saveBtnContainer) this._saveBtnContainer.hidden = true;
		if (this._submitBtnContainer) this._submitBtnContainer.hidden = true;

		const attendees = (this._currentClass.attendees || [])
			.filter(isResultable)
			.map((a) => ({ id: a.id, userId: a.userId, btmNumber: a.btmNumber, status: a.status }));

		try {
			const res = await Request.post(
				this._saveAttendeesApiUrl,
				{
					classId: this._currentClass.id,
					userId: this._currentClass.userId,
					btmNumber: this._currentClass.btmNumber,
					attendees,
					submitResults: submit
				}
			);

			const canSubmit = res !== 'false';

			if (submit && canSubmit) {
				await this._changeClassStatus('Completed');
				this._showResultMessage(this._resultMsg, 'Your results have been submitted successfully');
				this._updateAttendeesUI();
			} else if (submit && !canSubmit) {
				this._showResultMessage(this._resultMsg, 'Cannot submit results because not all attendees have a status of Passed, Failed or Did Not Attend');
			} else {
				this._showResultMessage(this._resultMsg, 'Your results have been saved successfully');
			}
		} catch (error) {
			console.error('Failed to save results:', error);
			this._showResultMessage(this._resultMsg, 'An error occurred');
		} finally {
			this._updateSaveResultsUI();
		}
	}

	async _saveComment(attendeeId, comment, tabKey) {
		const attendee = (this._currentClass.attendees || []).find((a) => a.id === attendeeId);
		if (!attendee || attendee.comment === comment) return;
		try {
			await Request.post(
				this._attendeeCommentsApiUrl,
				{ attendeeId, classId: this._currentClass.id, comments: comment }
			);
			attendee.comment = comment;
			const toggle = this._wrapper.querySelector(`.js-comment-toggle[data-attendee-id="${attendeeId}"]`);
			if (toggle) toggle.classList.toggle('has-comment', !!comment);
		} catch (error) {
			console.error('Failed to save comment:', error);
		}
	}

	async _changeClassStatus(newStatus) {
		this._statusUpdating.hidden = false;
		this._statusSelect.hidden = true;
		this._statusReadonly.hidden = true;
		this._editLink.hidden = true;

		try {
			await Request.post(
				this._classStatusUpdateApiUrl,
				{
					classStatus: newStatus,
					classId: this._currentClass.id
				}
			);
			this._currentClass.status = newStatus;
		} catch (error) {
			console.error('Failed to update class status:', error);
		} finally {
			this._statusUpdating.hidden = true;
			this._updateStatusUI();
			this._updateActionsUI();
			this._updateAttendeesUI();
			this._updateSaveResultsUI();
			this._updateAddAttendeeBtnUI();
		}
	}

	async _fetchClassDetails() {
		try {
			const response = await Request.get(this._classDetailsApiUrl, { classId: this._classID });
			this._currentClass = response?.responseContainerContents?.class || {};
			if (this._currentClass?.status) this._currentClass.status = this._currentClass.status.replace(/ /g, '');
		} catch (error) {
			this._currentClass = {};
		} finally {
			this._wrapperLoading.hidden = true;
			const hasData = Object.keys(this._currentClass).length > 0;
			this._classDetailsContainer.hidden = !hasData;
			this._wrapperEmpty.hidden = hasData;
			if (hasData) {
				this._updateClassInfoUI();
				this._updateStatusUI();
				this._updateActionsUI();
				this._updateAttendeesUI();
				this._updateSaveResultsUI();
				this._updateAddAttendeeBtnUI();
			}
		}
	}
}

const globalCourseProvidersClassDetails = new CourseProvidersClassDetails();
export default globalCourseProvidersClassDetails;
